import { useState, useRef, useEffect, useCallback } from 'react';
import { AirlineLogo } from '../ui/AirlineLogo';
import { RouteDisplay } from '../ui/RouteDisplay';
import { Button, SectionLabel } from '@perch/ui';
import type { ParsedLookupResult } from '../../lib/types';

const BARCODE_BARS = Array.from({ length: 40 }, () => ({
  width: Math.random() > 0.5 ? '2px' : '1px',
  height: `${12 + Math.random() * 8}px`,
}));

const CONFIDENCE_THRESHOLD = 0.8;
const HINT_KEY = 'perch_parse_preview_hint_seen';

interface ParsePreviewProps {
  data: ParsedLookupResult;
  onChange: (data: ParsedLookupResult) => void;
  onConfirm: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
}

/**
 * Editable boarding-pass-style preview card. All add-booking input
 * methods (email forward, PNR lookup, manual entry) converge here.
 *
 * Visual:
 *  - Dark theme matching existing BoardingPassPreview
 *  - Inline editing: tap a field → underline + cursor → tap away to save
 *  - Low-confidence fields: amber underline (always visible, invites editing)
 *  - One-time hint: "Tap any field to edit" on first view
 */
export function ParsePreview({ data, onChange, onConfirm, onBack, isSubmitting }: ParsePreviewProps) {
  const firstSegment = data.segments[0];
  const lastSegment = data.segments[data.segments.length - 1];
  const [showHint, setShowHint] = useState(() => {
    try { return !localStorage.getItem(HINT_KEY); } catch { return true; }
  });

  const dismissHint = useCallback(() => {
    setShowHint(false);
    try { localStorage.setItem(HINT_KEY, '1'); } catch { /* noop */ }
  }, []);

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric',
      });
    } catch { return '---'; }
  };

  const formatTime = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleTimeString('en-US', {
        hour: '2-digit', minute: '2-digit', hour12: true,
      });
    } catch { return '---'; }
  };

  const isLowConfidence = (field: string) => {
    if (!data.field_confidence) return false;
    return (data.field_confidence[field] ?? 1) < CONFIDENCE_THRESHOLD;
  };

  const updateSegment = (index: number, field: string, value: string) => {
    dismissHint();
    const newSegments = [...data.segments];
    newSegments[index] = { ...newSegments[index], [field]: value };
    onChange({ ...data, segments: newSegments });
  };

  const updateField = (field: keyof ParsedLookupResult, value: string | number | boolean) => {
    dismissHint();
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="boarding-pass-enter space-y-4">
      {/* Main boarding pass card */}
      <div className="relative bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 rounded-2xl overflow-hidden shadow-xl">
        {/* Perforated edge */}
        <div className="absolute left-0 top-0 bottom-0 w-4 flex flex-col justify-around py-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="w-2 h-2 bg-stone-100 dark:bg-stone-50 rounded-full -ml-1" />
          ))}
        </div>

        {/* Header */}
        <div className="pl-6 pr-4 pt-4 pb-3 border-b border-stone-700/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AirlineLogo code={data.airline_code} size={36} />
              <div>
                <p className="text-white font-bold text-lg tracking-wide">BOOKING PREVIEW</p>
                <p className="text-stone-400 text-xs">Confirm Details</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-stone-400 text-xs">CONFIRMATION</p>
              <p className="text-white font-mono font-bold text-xl tracking-widest">{data.pnr}</p>
            </div>
          </div>
        </div>

        {/* Route section */}
        <div className="pl-6 pr-4 py-5">
          <div className="flex items-center justify-between">
            <div className="text-center">
              <EditableField
                value={firstSegment?.origin || '---'}
                onChange={(v) => updateSegment(0, 'origin', v.toUpperCase())}
                lowConfidence={isLowConfidence('origin')}
                className="text-white font-mono text-3xl sm:text-4xl font-bold tracking-wider"
                maxLength={3}
                ariaLabel="Origin airport"
              />
              <p className="text-stone-400 text-xs mt-1">DEPART</p>
            </div>

            <div className="flex-1 px-4 sm:px-6">
              <div className="relative flex items-center justify-center">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-stone-600 to-transparent" />
                <div className="absolute bg-stone-800 px-2">
                  <svg className="w-8 h-8 text-primary" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                  </svg>
                </div>
              </div>
              <p className="text-center text-stone-500 text-xs mt-2">
                {data.segments.length > 1 ? `${data.segments.length} flights` : 'Direct'}
              </p>
            </div>

            <div className="text-center">
              <EditableField
                value={lastSegment?.destination || '---'}
                onChange={(v) => updateSegment(data.segments.length - 1, 'destination', v.toUpperCase())}
                lowConfidence={isLowConfidence('destination')}
                className="text-white font-mono text-3xl sm:text-4xl font-bold tracking-wider"
                maxLength={3}
                ariaLabel="Destination airport"
              />
              <p className="text-stone-400 text-xs mt-1">ARRIVE</p>
            </div>
          </div>
        </div>

        {/* Flight details grid */}
        <div className="pl-6 pr-4 pb-4 grid grid-cols-3 gap-4">
          <div>
            <p className="text-stone-500 text-xs mb-1">DATE</p>
            <p className="text-white font-medium text-sm">
              {firstSegment ? formatDate(firstSegment.departure_datetime) : '---'}
            </p>
          </div>
          <div>
            <p className="text-stone-500 text-xs mb-1">TIME</p>
            <p className="text-white font-medium text-sm">
              {firstSegment ? formatTime(firstSegment.departure_datetime) : '---'}
            </p>
          </div>
          <div>
            <p className="text-stone-500 text-xs mb-1">FLIGHT</p>
            <EditableField
              value={firstSegment ? `${firstSegment.carrier_code}${firstSegment.flight_number}` : '---'}
              onChange={(v) => {
                const carrier = v.slice(0, 2).toUpperCase();
                const num = v.slice(2);
                updateSegment(0, 'carrier_code', carrier);
                updateSegment(0, 'flight_number', num);
              }}
              lowConfidence={isLowConfidence('flight_number')}
              className="text-white font-medium text-sm"
              ariaLabel="Flight number"
            />
          </div>
        </div>

        {/* Price section */}
        <div className="pl-6 pr-4 py-4 bg-gradient-to-r from-primary/20 via-primary/10 to-transparent border-t border-stone-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-stone-400 text-xs">PRICE PAID</p>
              <EditableField
                value={
                  data.is_award_booking
                    ? `${(data.points_used ?? 0).toLocaleString()} pts`
                    : `${data.currency ?? 'USD'} ${(data.total_price_paid ?? 0).toLocaleString()}`
                }
                onChange={(v) => {
                  const num = parseFloat(v.replace(/[^0-9.]/g, ''));
                  if (!isNaN(num)) {
                    if (data.is_award_booking) {
                      updateField('points_used', Math.round(num));
                    } else {
                      updateField('total_price_paid', num);
                    }
                  }
                }}
                lowConfidence={isLowConfidence('total_price_paid')}
                className="text-white font-bold text-2xl font-data tabular-nums"
                ariaLabel="Price paid"
              />
            </div>
            {data.is_award_booking && (
              <span className="px-3 py-1.5 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-xs font-bold rounded-full shadow-lg">
                AWARD
              </span>
            )}
          </div>
        </div>

        {/* Barcode */}
        <div className="pl-6 pr-4 py-3 bg-stone-950/50">
          <div className="flex items-center gap-1">
            {BARCODE_BARS.map((bar, i) => (
              <div key={i} className="bg-stone-600" style={bar} />
            ))}
          </div>
        </div>
      </div>

      {/* Multi-segment detail */}
      {data.segments.length > 1 && (
        <div className="space-y-2">
          <SectionLabel>All Segments</SectionLabel>
          {data.segments.map((seg, i) => (
            <div
              key={i}
              className="flex items-center gap-3 p-3 bg-stone-100 dark:bg-stone-800 rounded-lg border border-stone-200 dark:border-stone-700"
            >
              <div className="w-8 h-8 bg-white dark:bg-stone-900 rounded-full flex items-center justify-center text-sm font-bold text-stone-600 dark:text-stone-400">
                {i + 1}
              </div>
              <div className="flex-1">
                <p className="font-medium text-stone-900 dark:text-stone-100">
                  {seg.carrier_code}{seg.flight_number}
                </p>
                <RouteDisplay
                  segments={[{ origin: seg.origin, destination: seg.destination }]}
                  size="sm"
                />
              </div>
              <div className="text-right text-sm text-stone-500 dark:text-stone-400">
                {formatDate(seg.departure_datetime)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Hint */}
      {showHint && (
        <p className="text-center text-xs text-stone-400 dark:text-stone-500">
          Tap any field to edit
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="flex-1 py-3 border border-stone-200/60 dark:border-stone-700/50 text-stone-600 dark:text-stone-400 rounded-full font-medium hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
        >
          Edit
        </button>
        <Button onClick={onConfirm} disabled={isSubmitting} size="lg" className="flex-1">
          {isSubmitting ? 'Saving...' : 'Track This Flight'}
        </Button>
      </div>
    </div>
  );
}

/**
 * Inline-editable field on the dark boarding pass. Tap to edit,
 * tap away to save. Low-confidence fields show a persistent amber underline.
 */
function EditableField({
  value,
  onChange,
  lowConfidence,
  className = '',
  maxLength,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  lowConfidence?: boolean;
  className?: string;
  maxLength?: number;
  ariaLabel?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setEditValue(value); }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (editValue.trim() !== value) {
      onChange(editValue.trim());
    }
  };

  const underlineClass = editing
    ? 'border-b border-white'
    : lowConfidence
      ? 'border-b-2 border-amber-400/50'
      : '';

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={editValue}
        onChange={(e) => setEditValue(maxLength ? e.target.value.slice(0, maxLength) : e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setEditValue(value); setEditing(false); } }}
        maxLength={maxLength}
        aria-label={ariaLabel}
        className={`${className} ${underlineClass} bg-transparent outline-none w-full`}
      />
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      aria-label={ariaLabel ? `Edit ${ariaLabel}` : undefined}
      aria-description={lowConfidence ? 'This field may need correction' : undefined}
      className={`${className} ${underlineClass} text-left cursor-text hover:border-b hover:border-stone-500 transition-colors`}
    >
      {value}
    </button>
  );
}
